import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { Select } from '../select';
import { SecondaryButton } from '../secondary-button';
import { BasicInput } from '../basic-input';
import { BILLING_DATA_TYPES } from '../../models/BillingData';
import { Validator } from '../../helpers/Validator';
import { BillingStore } from '../../stores/BillingStore';
import { ERROR_CODES } from '../../models/ApiError';
import styles from './BillingDataForm.css';
import { BaseButton } from '../base-button';
import { Modal } from '../modal';
import { SimpleButton } from '../simple-button';
import { Spinner } from '../spinner';
import { LocationStore } from '../../stores/LocationStore';

const IPropTypes = {
  buttonText: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
  onDeletion: PropTypes.func,
  billing: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    rut: PropTypes.string,
    personalId: PropTypes.string,
    locality: PropTypes.string,
    department: PropTypes.string,
  }),
};

const defaultProps = {
  billing: null,
  onSubmit: () => {},
  onDeletion: () => {},
};

const BillingTypeOptions = [
  {
    label: 'RUT',
    value: BILLING_DATA_TYPES.RUT,
  },
  {
    label: 'Cédula',
    value: BILLING_DATA_TYPES.PERSONALID,
  },
  {
    label: 'Otros',
    value: BILLING_DATA_TYPES.OTHER,
  },
];

class BillingDataForm extends React.Component {
  constructor(props) {
    super(props);
    const billing = props.billing || {};
    this.state = {
      selectedType: billing.type || null,

      socialReason: billing.name || null,
      rut: billing.rut || null,
      personalId: billing.personalId || null,
      address: billing.address || null,
      fullName: billing.name || null,

      rutValid: true,
      personalIdValid: true,

      showDeleteModal: false,

      locality: billing.locality || null,
      department: billing.department || null,
    };

    this._updateSelectedType = this._updateSelectedType.bind(this);
    this._updateFullName = this._updateFullName.bind(this);
    this._updateSocialReason = this._updateSocialReason.bind(this);
    this._updateRUT = this._updateRUT.bind(this);
    this._updatePersonalId = this._updatePersonalId.bind(this);
    this._updateOtherPersonalId = this._updateOtherPersonalId.bind(this);
    this._updateAddress = this._updateAddress.bind(this);
    this._updateLocality = this._updateLocality.bind(this);
    this._updateDepartment = this._updateDepartment.bind(this);
    this._dataIsValid = this._dataIsValid.bind(this);
    this._submitCreateData = this._submitCreateData.bind(this);
    this._submitEditData = this._submitEditData.bind(this);
    this._deleteBillingData = this._deleteBillingData.bind(this);
    this._showDeleteModal = this._showDeleteModal.bind(this);
    this._closeDeleteModal = this._closeDeleteModal.bind(this);
    this._toggleSuccessButton = this._toggleSuccessButton.bind(this);
    this._toggleErrorButton = this._toggleErrorButton.bind(this);
  }

  async componentDidMount() {
    if (this.props.billing) {
      const dep = (LocationStore.departments || [])
        .find(x => x.name === this.props.billing.department);
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        department: dep,
      });
      await LocationStore.getLocations(dep.id);
      const loc = (LocationStore.locations || [])
        .find(x => x.name === this.props.billing.locality);
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        locality: loc,
      });
    }
  }

  componentWillUnmount() {
    BillingStore.resetErrors();
  }

  _updateSelectedType(type) {
    const personalIdValid = this.state.personalId !== null
      ? Validator.isUruguayanId(this.state.personalId)
      : true;
    const rutValid = this.state.rut !== null
      ? Validator.isUruguayanRUTNew(this.state.rut)
      : true;

    switch (type) {
      case BILLING_DATA_TYPES.RUT:
        this.setState({
          selectedType: type,
          rutValid,
        });
        break;
      case BILLING_DATA_TYPES.PERSONALID:
        this.setState({
          selectedType: type,
          personalIdValid,
        });
        break;
      default:
        this.setState({
          selectedType: type,
        });
    }
  }

  _updateFullName(event) {
    const { target: { value } } = event;
    this.setState({
      fullName: value,
    });
  }

  _updateSocialReason(event) {
    const { target: { value } } = event;
    this.setState({
      socialReason: value,
    });
  }

  _updateRUT(event) {
    const { target: { value } } = event;
    // Only numeric characters allowed
    if (!/^\d*$/.test(value)) {
      return;
    }
    BillingStore.clearSubmitApiError();
    this.setState({
      rut: value,
      rutValid: Validator.isUruguayanRUTNew(value),
    });
  }

  _updatePersonalId(event) {
    const { target: { value } } = event;
    // Only alpha-numeric characters allowed
    if (!/^\w*$/.test(value)) {
      return;
    }
    BillingStore.clearSubmitApiError();
    this.setState({
      personalId: value,
      personalIdValid: Validator.isUruguayanId(value),
    });
  }

  _updateOtherPersonalId(event) {
    const { target: { value } } = event;
    this.setState({
      personalId: value,
    });
  }

  _updateAddress(event) {
    const { target: { value } } = event;
    this.setState({
      address: value,
    });
  }

  _updateLocality(locality) {
    this.setState({
      locality: (LocationStore.locations || []).find(x => x.id === locality),
    });
  }

  _updateDepartment(department) {
    this.setState({
      department: LocationStore.departments.find(x => x.id === department),
    });
    LocationStore.getLocations(department);
  }

  _dataIsValid() {
    let specificDataIsValid = true;
    if (this.state.selectedType === BILLING_DATA_TYPES.RUT) {
      specificDataIsValid = (
        !!this.state.socialReason && !!this.state.socialReason.replace(/ /g, '') &&
        !!this.state.rut && !!this.state.socialReason.replace(/ /g, '') &&
        this.state.rutValid &&
        !!this.state.address && !!this.state.address.replace(/ /g, '') &&
        !!this.state.locality && !!this.state.locality.name && !!this.state.locality.name.replace(/ /g, '') &&
        !!this.state.department && !!this.state.department.name && !!this.state.department.name.replace(/ /g, '')
      );
    } else if (this.state.selectedType === BILLING_DATA_TYPES.PERSONALID) {
      specificDataIsValid = (
        !!this.state.personalId && !!this.state.personalId.replace(/ /g, '') &&
        this.state.personalIdValid &&
        !!this.state.fullName && !!this.state.fullName.replace(/ /g, '') &&
        !!this.state.address && !!this.state.address.replace(/ /g, '') &&
        !!this.state.locality && !!this.state.locality.name && !!this.state.locality.name.replace(/ /g, '') &&
        !!this.state.department && !!this.state.department.name && !!this.state.department.name.replace(/ /g, '')
      );
    } else {
      specificDataIsValid = (
        !!this.state.address && !!this.state.address.replace(/ /g, '') &&
        !!this.state.fullName && !!this.state.fullName.replace(/ /g, '') &&
        !!this.state.personalId && !!this.state.personalId.replace(/ /g, '') &&
        !!this.state.selectedType && !!this.state.selectedType.replace(/ /g, '') &&
        !!this.state.locality && !!this.state.locality.name && !!this.state.locality.name.replace(/ /g, '') &&
        !!this.state.department && !!this.state.department.name && !!this.state.department.name.replace(/ /g, '')
      );
    }
    return specificDataIsValid;
  }

  async _submitCreateData(event) {
    if (event) {
      event.preventDefault();
    }
    if (!this._dataIsValid()) {
      return;
    }
    const billingData = await BillingStore.createBillingDataV2(this.state.selectedType, {
      socialReason: this.state.socialReason,
      rut: this.state.rut,
      personalId: this.state.personalId,
      address: this.state.address,
      fullName: this.state.fullName,
      locality: this.state.locality.name,
      department: this.state.department.name,
    });
    if (!BillingStore.submitError &&
      !BillingStore.submitApiError &&
      !BillingStore.submitInternalError) {
      this.props.onSubmit(billingData);
    }
  }

  async _submitEditData(event) {
    if (event) {
      event.preventDefault();
    }
    if (!this._dataIsValid()) {
      return;
    }
    const billingData = await BillingStore.editBillingDataV2(
      this.props.billing.id,
      this.state.selectedType, {
        socialReason: this.state.socialReason,
        rut: this.state.rut,
        personalId: this.state.personalId,
        address: this.state.address,
        fullName: this.state.fullName,
        locality: this.state.locality.name,
        department: this.state.department.name,
      },
    );
    if (!BillingStore.submitError &&
      !BillingStore.submitApiError &&
      !BillingStore.submitInternalError) {
      this.props.onSubmit(billingData);
    }
  }

  _showDeleteModal(event) {
    event.preventDefault();
    this.setState({ showDeleteModal: true });
  }

  _closeDeleteModal(event) {
    event.preventDefault();
    this.setState({ showDeleteModal: false });
  }

  _deleteBillingData(event) {
    event.preventDefault();
    BillingStore.deleteBillingData(this.props.billing.id);
  }

  _toggleSuccessButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });
    BillingStore.clearDeletionSuccess();
    this.props.onDeletion();
  }

  _toggleErrorButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });
    BillingStore.clearDeletionErrors();
  }

  render() {
    let rutError = null;
    if (this.state.rut === '') {
      rutError = 'El RUT no puede ser vacío';
    } else if (!this.state.rutValid ||
        BillingStore.submitApiError === ERROR_CODES.INVALID_RUT) {
      rutError = 'El RUT es inválido';
    }

    let personalIdError = null;
    if (this.state.personalId === '' || (this.state.personalId && this.state.personalId.replace(/ /g, '') === '')) {
      personalIdError = 'Este campo no puede ser vacío';
    } else if (!this.state.personalIdValid ||
        BillingStore.submitApiError === ERROR_CODES.INVALID_URUGUAYAN_DOCUMENT) {
      personalIdError = 'El documento es inválido';
    }

    const fullNameError = this.state.fullName && this.state.fullName.replace(/ /g, '') === '' ? 'Este campo no puede ser vacío' : null;
    const addressError = this.state.address && this.state.address.replace(/ /g, '') === '' ? 'Este campo no puede ser vacío' : null;
    const socialReasonError = this.state.socialReason && this.state.socialReason.replace(/ /g, '') === '' ? 'Este campo no puede ser vacío' : null;
    const otherPersonalIdError = this.state.personalId && this.state.personalId.replace(/ /g, '') === '' ? 'Este campo no puede ser vacío' : null;

    const userType = this.state.selectedType !== null
      ? BillingTypeOptions.find(type => type.value === this.state.selectedType)
      : 'Seleccionar';

    const isTypeSelected = userType !== 'Seleccionar';

    const departmentOptions = LocationStore.departments.map(departmentData => ({
      value: departmentData.id,
      label: departmentData.name,
    }));
    const locationOptions = (LocationStore.locations || []).map(locationData => ({
      value: locationData.id,
      label: locationData.name,
    }));

    return (
      <div className={styles.billingDataForm}>
        <form
          onSubmit={
            this.props.billing === null
            ? this._submitCreateData
            : this._submitEditData
          }
        >
          <label className={styles.selectLabel}>
            Tipo
          </label>
          <Select
            className={styles.select}
            options={BillingTypeOptions}
            onChange={this._updateSelectedType}
            value={isTypeSelected ? userType.label : userType}
            selectedClassName={styles.selectValue}
          />
          {
            isTypeSelected &&
            userType.value !== BILLING_DATA_TYPES.RUT &&
            <div>
              {
                userType.value === BILLING_DATA_TYPES.PERSONALID &&
                <BasicInput
                  className={styles.input}
                  label="Número de Documento"
                  name="personalId"
                  onChange={this._updatePersonalId}
                  error={personalIdError}
                  value={this.state.personalId || ''}
                />
              }
              {
                userType.value === BILLING_DATA_TYPES.OTHER &&
                <BasicInput
                  className={styles.input}
                  label="Número de Documento"
                  name="personalId"
                  onChange={this._updateOtherPersonalId}
                  error={otherPersonalIdError}
                  value={this.state.personalId || ''}
                />
              }
              <BasicInput
                className={styles.input}
                label="Nombre Completo"
                name="fullName"
                onChange={this._updateFullName}
                error={fullNameError}
                value={this.state.fullName || ''}
              />
            </div>
          }
          {
            isTypeSelected &&
            userType.value === BILLING_DATA_TYPES.RUT &&
            <div>
              <BasicInput
                className={styles.input}
                label="Rázon Social"
                name="socialReason"
                onChange={this._updateSocialReason}
                error={socialReasonError}
                value={this.state.socialReason || ''}
              />
              <BasicInput
                className={styles.input}
                label="RUT"
                name="rut"
                onChange={this._updateRUT}
                error={rutError}
                value={this.state.rut || ''}
              />
            </div>
          }

          {
            isTypeSelected &&
            <div
              style={{ marginTop: 20 }}
              className={styles.selectGroupContainer}
            >
              <label className={styles.selectLabel}>
                Departamento
              </label>
              <Select
                className={styles.select}
                onChange={this._updateDepartment}
                options={departmentOptions}
                value={this.state.department ? this.state.department.name : 'Seleccionar'}
                // error={departmentError}
              />
            </div>
          }
          {
            isTypeSelected &&
            <div
              className={styles.selectGroupContainer}
            >
              <label className={styles.selectLabel}>
                Localidad
              </label>
              <Select
                className={styles.select}
                disabled={!LocationStore.locations}
                onChange={this._updateLocality}
                options={locationOptions}
                value={this.state.locality ? this.state.locality.name : 'Seleccionar'}
                // error={localityError}
              />
            </div>
          }
          {
            isTypeSelected &&
            <BasicInput
              className={styles.input}
              label="Dirección"
              name="address"
              onChange={this._updateAddress}
              error={addressError}
              value={this.state.address || ''}
            />
          }
          {
            !!BillingStore.creationError &&
            <p className={styles.error}>
              Ha ocurrido un error, por favor intente nuevamente.
            </p>
          }
          <SecondaryButton
            className={styles.formButton}
            disabled={!this._dataIsValid()}
          >
            {this.props.buttonText}
          </SecondaryButton>
        </form>
        {
          BillingStore.submitInternalError &&
          <p className={styles.error}>
            Estamos experimentando algunas dificultades en nuestro sitio.
            Por favor intente nuevamente en unos momentos.
          </p>
        }
        {
          BillingStore.submitError &&
          <p className={styles.error}>
            Ha ocurrido un error, por favor intente nuevamente.
          </p>
        }
        {
          !!this.props.billing &&
          <BaseButton
            className={styles.deleteButton}
            onClick={this._showDeleteModal}
          >
            Borrar datos de facturación
          </BaseButton>
        }
        {
        this.state.showDeleteModal &&
          <Modal>
            <div className={styles.modalChildren}>
              {
                BillingStore.deletionError &&
                <div>
                  <p className={styles.modalError}>
                    Ha ocurrido un error, por favor intente nuevamente.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                BillingStore.deletionInternalError &&
                <div>
                  <p className={styles.modalError}>
                    Estamos experimentando algunas dificultades en nuestro sitio.
                    Por favor intente nuevamente en unos momentos.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                BillingStore.deletionSuccess &&
                <div>
                  <p className={styles.modalSuccess}>
                    Datos de facturación eliminados con éxito
                  </p>
                  <SimpleButton
                    onClick={this._toggleSuccessButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                BillingStore.deletingBillingData &&
                <Spinner className={styles.modalSpinner} />
              }
              {
                !BillingStore.deletionError &&
                !BillingStore.deletionInternalError &&
                !BillingStore.deletingBillingData &&
                !BillingStore.deletionSuccess &&
                <div>
                  <p className={styles.modalQuestion}>
                    ¿Está seguro que desea borrar estos datos de facturación?
                  </p>
                  <p className={styles.modalText}>
                    Esta operación no puede deshacerse.
                  </p>
                  <div className={styles.modalButtonsSection}>
                    <SimpleButton
                      className={styles.hideModalButton}
                      onClick={this._closeDeleteModal}
                    >
                      Cancelar
                    </SimpleButton>
                    <SimpleButton
                      className={styles.deleteModalButton}
                      onClick={this._deleteBillingData}
                    >
                      Borrar
                    </SimpleButton>
                  </div>
                </div>
              }
            </div>
          </Modal>
        }
      </div>
    );
  }
}

BillingDataForm.propTypes = IPropTypes;
BillingDataForm.defaultProps = defaultProps;

const ObservedBillingDataForm = observer(BillingDataForm);

export { ObservedBillingDataForm as BillingDataForm };



// WEBPACK FOOTER //
// ./src/common/billing-data-form/BillingDataForm.jsx